/**
 * Hand-written control-plane client for the profile "sync burst" endpoint (#443).
 *
 * The orval generator fetches its OpenAPI spec from a running control-plane API and rewrites the
 * whole `src/api/control` folder (clean: true), so this hook lives here, outside that folder, to
 * survive regeneration. Once the spec is regenerated with the burst endpoint, this file can be
 * replaced by the generated `usePostProfileBurst`. It intentionally mirrors the shape of the
 * generated mutation hooks (see `postProfileSync` / `usePostProfileSync`).
 */
import { useMutation } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';

import type { ErrorProblemDetails } from './control/missionImpactAPI.schemas';
import { instance } from './mutator-control-plane';
import type { ErrorType } from './mutator-control-plane';

/**
 * Request body for activating or clearing a profile sync burst. A positive `windowHours` activates
 * the burst until `now + windowHours` (capped server-side at the maximum burst window); null or a
 * non-positive value clears any active burst.
 */
export interface BurstRequest {
  windowHours?: number | null;
}

/** Response body: the applied burst expiry (ISO-8601 UTC), or null when the burst was cleared. */
export interface PostProfileBurst200 {
  burstExpiresUtc?: string | null;
}

/**
 * Reads a profile's burst expiry. The generated profile DTO type does not yet carry
 * `burstExpiresUtc` (its OpenAPI spec has not been regenerated), but the API response does include
 * it at runtime, so this reads it defensively without an unsafe cast.
 */
export function readBurstExpiresUtc(profile: unknown): string | null {
  if (profile && typeof profile === 'object' && 'burstExpiresUtc' in profile) {
    const value = (profile as { burstExpiresUtc?: unknown }).burstExpiresUtc;
    return typeof value === 'string' ? value : null;
  }
  return null;
}

/** Variables accepted by the burst mutation. */
export type PostProfileBurstMutationVariables = {
  orgId: string;
  profileId: string;
  data: BurstRequest;
};

/**
 * Activates or clears an accelerated sync burst for a profile.
 * @summary Activate or clear sync burst
 */
export const postProfileBurst = (
  orgId: string,
  profileId: string,
  burstRequest: BurstRequest,
  signal?: AbortSignal
) => {
  return instance<PostProfileBurst200>({
    url: `/orgs/${orgId}/profiles/${profileId}/burst`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: burstRequest,
    signal,
  });
};

export const getPostProfileBurstMutationOptions = <
  TError = ErrorType<ErrorProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postProfileBurst>>,
    TError,
    PostProfileBurstMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postProfileBurst>>,
  TError,
  PostProfileBurstMutationVariables,
  TContext
> => {
  const mutationKey = ['postProfileBurst'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postProfileBurst>>,
    PostProfileBurstMutationVariables
  > = (props) => {
    const { orgId, profileId, data } = props ?? ({} as PostProfileBurstMutationVariables);

    return postProfileBurst(orgId, profileId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostProfileBurstMutationResult = NonNullable<
  Awaited<ReturnType<typeof postProfileBurst>>
>;
export type PostProfileBurstMutationBody = BurstRequest;
export type PostProfileBurstMutationError = ErrorType<ErrorProblemDetails>;

/**
 * @summary Activate or clear sync burst
 */
export const usePostProfileBurst = <TError = ErrorType<ErrorProblemDetails>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postProfileBurst>>,
      TError,
      PostProfileBurstMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof postProfileBurst>>,
  TError,
  PostProfileBurstMutationVariables,
  TContext
> => {
  const mutationOptions = getPostProfileBurstMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
